import { useEffect, useState } from "react";
import axios from "axios";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tiny in-memory cache so repeated SPA navigations don't refetch overrides.
const overrideCache = new Map();

// Module-level "claim" registry — when this hook is called by both the global AppShell
// applier AND a per-page instance for the same path, we must ensure ONLY ONE set of
// MutationObservers gets installed per DOM element. Otherwise the two observers can
// bounce values back-and-forth on aliased URLs, freezing the browser.
// Each claim is a { path, token } where token is a monotonically-increasing counter.
// The FIRST instance to mount for a given path wins and owns the observers.
const activeClaims = new Map(); // path -> token
let _claimCounter = 0;
function claimPath(path) {
  if (!path) return null;
  if (activeClaims.has(path)) return null; // already claimed by another instance
  const token = ++_claimCounter;
  activeClaims.set(path, token);
  return token;
}
function releaseClaim(path, token) {
  if (path && token && activeClaims.get(path) === token) {
    activeClaims.delete(path);
  }
}

/**
 * usePageSEOOverride — read SEO override from DB (set by the SEO agency portal),
 * fall back to hardcoded defaults, and apply meta_title / meta_description / canonical.
 *
 * Also imperatively injects og:title, og:description, og:image and raw schema JSON-LD
 * into <head> so the SEO agency can dynamically change those without a redeploy.
 *
 * Usage:
 *   usePageSEOOverride("/", {
 *     title: "Ezeefit — Modular Office Furniture Manufacturer Mumbai",
 *     description: "…",
 *     canonical: "https://ezeefitofficesystems.in/"
 *   });
 *
 * Returns the override object (or null) so the page can also read h1 overrides etc.
 */
export default function usePageSEOOverride(path, defaults = {}) {
  const [override, setOverride] = useState(null);
  const [claim, setClaim] = useState(null);

  // Try to claim this path so only ONE instance installs observers.
  // Losing instances still fetch override state (so they can return it to the page)
  // but skip all DOM-mutation effects.
  useEffect(() => {
    if (!path) return undefined;
    const token = claimPath(path);
    setClaim(token);
    return () => {
      releaseClaim(path, token);
      setClaim(null);
    };
  }, [path]);

  const isOwner = Boolean(claim);

  useEffect(() => {
    let cancelled = false;
    if (!path) {
      setOverride(null);
      return;
    }
    if (overrideCache.has(path)) {
      const cached = overrideCache.get(path);
      setOverride(cached);
      return;
    }
    axios
      .get(`${API}/seo-public/page-override`, { params: { path } })
      .then((r) => {
        if (cancelled) return;
        const data = r.data && Object.keys(r.data).length > 0 ? r.data : null;
        overrideCache.set(path, data);
        setOverride(data);
      })
      .catch(() => {
        overrideCache.set(path, null);
        if (!cancelled) setOverride(null);
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  const effectiveTitle = override?.meta_title || defaults.title || null;
  const effectiveDesc = override?.meta_description || defaults.description || null;
  const effectiveCanonical = override?.canonical || defaults.canonical || null;

  useCanonical(isOwner ? effectiveCanonical : null);
  useMetaDescription(isOwner ? effectiveDesc : null);

  // Imperative <title> control — bypass Helmet for reliability.
  // Uses MutationObserver to re-assert the title if any late-firing code
  // (e.g. per-page useEffect that sets document.title = hardcoded fallback)
  // clobbers it after this hook has applied Speicher's portal override.
  useEffect(() => {
    if (!isOwner || !effectiveTitle) return undefined;
    const previous = document.title;
    document.title = effectiveTitle;

    // Watch <title> element for external mutations and restore the override.
    const titleEl = document.querySelector("title");
    let observer = null;
    if (titleEl && typeof MutationObserver !== "undefined") {
      observer = new MutationObserver(() => {
        if (document.title !== effectiveTitle) {
          document.title = effectiveTitle;
        }
      });
      observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
    }
    return () => {
      if (observer) observer.disconnect();
      document.title = previous;
    };
  }, [effectiveTitle, isOwner]);

  // Bulletproof meta description — re-assert if late-firing code clobbers it.
  useEffect(() => {
    if (!isOwner || !effectiveDesc) return undefined;
    const metaEl = document.querySelector('meta[name="description"]');
    if (!metaEl || typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(() => {
      if (metaEl.getAttribute("content") !== effectiveDesc) {
        metaEl.setAttribute("content", effectiveDesc);
      }
    });
    observer.observe(metaEl, { attributes: true, attributeFilter: ["content"] });
    return () => observer.disconnect();
  }, [effectiveDesc, isOwner]);

  // Bulletproof canonical — re-assert if late-firing code clobbers it.
  useEffect(() => {
    if (!isOwner || !effectiveCanonical) return undefined;
    const linkEl = document.querySelector('link[rel="canonical"]');
    if (!linkEl || typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(() => {
      if (linkEl.getAttribute("href") !== effectiveCanonical) {
        linkEl.setAttribute("href", effectiveCanonical);
      }
    });
    observer.observe(linkEl, { attributes: true, attributeFilter: ["href"] });
    return () => observer.disconnect();
  }, [effectiveCanonical, isOwner]);

  // og:title / og:description / og:image — set/restore.
  useEffect(() => {
    if (!isOwner) return undefined;
    const ogs = [
      ["og:title", override?.og_title || defaults.ogTitle || effectiveTitle],
      ["og:description", override?.og_description || defaults.ogDescription || effectiveDesc],
      ["og:image", override?.og_image || defaults.ogImage || null],
    ];
    const restore = [];
    ogs.forEach(([prop, value]) => {
      if (!value) return;
      let tag = document.querySelector(`meta[property="${prop}"]`);
      const created = !tag;
      if (created) {
        tag = document.createElement("meta");
        tag.setAttribute("property", prop);
        document.head.appendChild(tag);
      }
      const prev = tag.getAttribute("content");
      tag.setAttribute("content", value);
      restore.push(() => {
        if (created && tag.parentNode) tag.parentNode.removeChild(tag);
        else if (prev !== null) tag.setAttribute("content", prev);
      });
    });
    return () => {
      restore.forEach((fn) => fn());
    };
  }, [override, effectiveTitle, effectiveDesc, defaults.ogTitle, defaults.ogDescription, defaults.ogImage, isOwner]);

  // Extended head tags — robots, og:*, twitter:*, geo:* — set/restore.
  // These fields have no hardcoded defaults; only apply when Speicher sets a value in the portal.
  useEffect(() => {
    if (!isOwner || !override) return undefined;
    // [selector-attr, selector-value, attr-name-holding-value, override-key]
    const tags = [
      ["name", "robots", override.robots],
      ["property", "og:type", override.og_type],
      ["property", "og:site_name", override.og_site_name],
      ["property", "og:url", override.og_url],
      ["property", "og:locale", override.og_locale],
      ["name", "twitter:card", override.twitter_card],
      ["name", "twitter:title", override.twitter_title],
      ["name", "twitter:description", override.twitter_description],
      ["name", "twitter:image", override.twitter_image],
      ["name", "twitter:label1", override.twitter_label1],
      ["name", "twitter:data1", override.twitter_data1],
      ["name", "twitter:label2", override.twitter_label2],
      ["name", "twitter:data2", override.twitter_data2],
      ["name", "geo.region", override.geo_region],
      ["name", "geo.placename", override.geo_placename],
      ["name", "geo.position", override.geo_position],
    ];
    const restore = [];
    tags.forEach(([selKey, selVal, value]) => {
      if (value === undefined || value === null || value === "") return;
      let tag = document.querySelector(`meta[${selKey}="${selVal}"]`);
      const created = !tag;
      if (created) {
        tag = document.createElement("meta");
        tag.setAttribute(selKey, selVal);
        document.head.appendChild(tag);
      }
      const prev = tag.getAttribute("content");
      tag.setAttribute("content", value);
      restore.push(() => {
        if (created && tag.parentNode) tag.parentNode.removeChild(tag);
        else if (prev !== null) tag.setAttribute("content", prev);
      });
    });
    return () => {
      restore.forEach((fn) => fn());
    };
  }, [override, isOwner]);

  // Schema JSON-LD injection (only if override defines one) + optional default-schema suppression.
  useEffect(() => {
    if (!isOwner || !path) return undefined;
    const id = `seo-override-schema-${path.replace(/\W+/g, "-")}`;
    let removedDefaults = [];

    // If editor asked to disable default schemas for this page, hide every
    // <script type="application/ld+json"> that wasn't injected by this hook.
    // We HIDE (not remove) so React can still own the original DOM nodes; we
    // mark them invisible to crawlers by setting type="application/ld+json-disabled"
    // which Google ignores.
    if (override?.disable_default_schema) {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach((s) => {
        if (s.id && s.id.startsWith("seo-override-schema-")) return; // never disable our own override
        s.setAttribute("type", "application/ld+json-disabled");
        removedDefaults.push(s);
      });
    }

    if (override?.schema_jsonld) {
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement("script");
        script.id = id;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      // Speicher (and Google's own docs) often paste JSON-LD wrapped in
      // <script type="application/ld+json">...</script> tags. If we set that
      // whole string as textContent of an already-<script> element, we end up
      // with a NESTED <script> in the DOM — broken HTML. Strip the wrapper.
      let payload = String(override.schema_jsonld).trim();
      // Remove leading HTML comments (e.g. <!-- Product schema -->)
      payload = payload.replace(/^\s*<!--[\s\S]*?-->\s*/g, "");
      // Strip a single leading <script ...> and trailing </script>
      payload = payload.replace(/^\s*<script[^>]*>\s*/i, "");
      payload = payload.replace(/\s*<\/script>\s*$/i, "");
      // Also strip JSON-escape variants (\x3c/script>, \u003c/script>) that leak
      // in from JSON-encoded exports
      payload = payload.replace(/\\x3c\s*\/\s*script\s*>?\s*$/i, "");
      payload = payload.replace(/\\u003c\s*\/\s*script\s*>?\s*$/i, "");
      payload = payload.replace(/^\s*\\x3c\s*script[^>]*>?\s*/i, "");
      payload = payload.replace(/^\s*\\u003c\s*script[^>]*>?\s*/i, "");
      script.textContent = payload.trim();
    }

    return () => {
      const existing = document.getElementById(id);
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      // Restore the default schemas we hid (so SPA navigation doesn't keep them disabled forever)
      removedDefaults.forEach((s) => s.setAttribute("type", "application/ld+json"));
    };
  }, [override, path, isOwner]);

  // H1 override — replace the FIRST <h1> on the page with Speicher's edited text.
  // Bulletproof: uses MutationObserver so late-firing React re-renders don't clobber it.
  useEffect(() => {
    if (!isOwner) return undefined;
    const newH1 = override?.h1;
    if (!newH1) return undefined;
    const h1 = document.querySelector("h1");
    if (!h1 || typeof MutationObserver === "undefined") return undefined;
    const previousHtml = h1.innerHTML;
    // An approved body-content edit on the H1 (marked data-speicher-override) takes priority.
    const contentEditWins = () => !!(h1.dataset.speicherOverride || h1.dataset.speicherEdited
      || h1.querySelector("[data-speicher-override],[data-speicher-edited]"));
    if (!contentEditWins() && h1.textContent !== newH1) h1.textContent = newH1;
    const observer = new MutationObserver(() => {
      if (contentEditWins()) {
        observer.disconnect();
        return;
      }
      if (h1.textContent !== newH1) {
        h1.textContent = newH1;
      }
    });
    observer.observe(h1, { childList: true, characterData: true, subtree: true });
    return () => {
      observer.disconnect();
      // Restore original markup on unmount so SPA navigation doesn't leak the override.
      h1.innerHTML = previousHtml;
    };
  }, [override?.h1, isOwner]);

  return override;
}
